import React, { useEffect, useMemo, useRef, useState, useCallback, useLayoutEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Search,
  ArrowLeft,
  Boxes,
  Home,
  User2,
  LogOut,
  ChevronDown,
  ChevronRight,
  ShoppingCart,
} from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

import SearchBox from "../SearchBox";
import logo from "../../../assets/Images/portakal11.png";

import { useCustomerAuth } from "../../../context/CustomerAuthContext/CustomerAuthContext";
import { useCart } from "../../../context/CartContext/CartContext";
import CartSheet from "../../../pages/Customer/CartPage/CartSheet/CartSheet";
import axiosInstance from "../../../api/axiosInstance";
import { postRequest } from "../../../api/postRequest";
import { ApiResponse } from "../../../types/Api";
import { useTheme } from "../../../context/ThemeContext";

/* ─────────────────────────────────────────────────────────
   Types
───────────────────────────────────────────────────────── */
type Category = {
  id: number;
  ad: string;
  ustKategoriId: number | null;
  slug: string;
  siraNo: number;
  aktifMi: boolean;
};

type CategoryNode = Category & { children: CategoryNode[] };

/* ─────────────────────────────────────────────────────────
   API
───────────────────────────────────────────────────────── */
async function fetchCategories(): Promise<Category[]> {
  try {
    const res = await axiosInstance.post<ApiResponse<Category[]>>("/Kategori/listele", {});
    const payload = res.data;

    if (payload?.success && Array.isArray(payload.data)) return payload.data;

    console.error("Kategori API yanıtı beklenen formatta değil:", payload);
    return [];
  } catch (err: any) {
    console.error("Kategori API hatası:", err?.response || err?.message || err);
    return [];
  }
}

/* ─────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────── */
function buildCategoryTree(list: Category[]): CategoryNode[] {
  const map = new Map<number, CategoryNode>();
  const roots: CategoryNode[] = [];

  list.forEach((c) => map.set(c.id, { ...c, children: [] }));

  map.forEach((n) => {
    if (n.ustKategoriId && map.has(n.ustKategoriId)) {
      map.get(n.ustKategoriId)!.children.push(n);
    } else {
      roots.push(n);
    }
  });

  const sort = (xs: CategoryNode[]) => {
    xs.sort(
      (a, b) =>
        (a.siraNo ?? 0) - (b.siraNo ?? 0) || a.ad.localeCompare(b.ad, "tr")
    );
    xs.forEach((x) => sort(x.children));
  };

  sort(roots);
  return roots;
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const m = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!m) return;
    const onChange = () => setReduced(!!m.matches);
    onChange();
    m.addEventListener?.("change", onChange);
    return () => m.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

type AnnouncementItem = { text: string; to?: string };
type HomeConfigResponseItem = { bilgiler?: string | null };
type HeaderConfig = {
  announcements?: AnnouncementItem[];
  brand?: { logoUrl?: string; siteName?: string; siteTagline?: string; tagline?: string };
  footer?: { companyName?: string; companyTagline?: string };
};

const defaultAnnouncementItems: AnnouncementItem[] = [
  { text: "1500 TL ve üzeri kargo ücretsiz", to: "/products" },
  { text: "Yeni sezon ürünler stokta", to: "/products" },
  { text: "Teknik servis & destek: Hemen ulaşın", to: "/iletisim" },
];

type FetchedHeaderConfig = {
  announcements: AnnouncementItem[];
  logoUrl: string | null;
  siteName: string | null;
  siteTagline: string | null;
};

async function fetchHeaderConfig(): Promise<FetchedHeaderConfig> {
  const fallback: FetchedHeaderConfig = {
    announcements: defaultAnnouncementItems,
    logoUrl: null,
    siteName: null,
    siteTagline: null,
  };
  try {
    const res = await postRequest<{}, ApiResponse<HomeConfigResponseItem[]>>("/AnaEkranVerileri/listele", {});
    const rawConfig = res?.data?.[0]?.bilgiler;

    if (!res?.success || !rawConfig) return fallback;

    const parsed = JSON.parse(rawConfig) as HeaderConfig;

    const items = Array.isArray(parsed?.announcements)
      ? parsed.announcements.filter(
          (item): item is AnnouncementItem =>
            !!item && typeof item.text === "string" && item.text.trim().length > 0
        )
      : [];

    return {
      announcements: items.length > 0 ? items : defaultAnnouncementItems,
      logoUrl: parsed?.brand?.logoUrl || null,
      siteName: parsed?.brand?.siteName || parsed?.footer?.companyName || null,
      siteTagline: parsed?.brand?.siteTagline || parsed?.footer?.companyTagline || null,
    };
  } catch (err: any) {
    console.error("Header config yüklenemedi:", err?.response || err?.message || err);
    return fallback;
  }
}

function cn(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(" ");
}
/* ─────────────────────────────────────────────────────────
   Announcement (kayan yazı / açıklama barı)
───────────────────────────────────────────────────────── */
export const AnnouncementBar: React.FC<{
  items: AnnouncementItem[];
  speedPxPerSec?: number; // default 90
  separator?: string;     // default "•"
  pauseOnHover?: boolean; // default true
}> = ({ items, speedPxPerSec = 90, separator = "•", pauseOnHover = true }) => {
  const reduced = useReducedMotion();

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const [wrapW, setWrapW] = useState(1);
  const [trackW, setTrackW] = useState(1);
  const [paused, setPaused] = useState(false);

  // ✅ ölçümü garanti et (ilk render 0 gelmesin)
  useLayoutEffect(() => {
    const measure = () => {
      const wrap = wrapRef.current;
      const track = trackRef.current;
      if (!wrap || !track) return;

      // scrollWidth: içerik genişliği
      const wW = Math.max(1, Math.round(wrap.clientWidth));
      const tW = Math.max(1, Math.round(track.scrollWidth));

      setWrapW(wW);
      setTrackW(tW);
    };

    // ilk ölçüm
    const raf = requestAnimationFrame(measure);

    // resize ile güncelle
    const ro = new ResizeObserver(() => measure());
    if (wrapRef.current) ro.observe(wrapRef.current);
    if (trackRef.current) ro.observe(trackRef.current);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [items]);

  // ✅ içerik kısa olsa bile boşluk kalmasın diye çoğalt
  const repeatCount = useMemo(() => {
    const minWidth = wrapW * 2; // en az 2 ekran dolsun
    const n = Math.ceil(minWidth / trackW);
    return Math.max(2, Math.min(n, 10));
  }, [wrapW, trackW]);

  // ✅ hız sabit: süre = trackW / speed
  const duration = useMemo(() => {
    const s = Math.max(40, speedPxPerSec);
    return trackW / s;
  }, [trackW, speedPxPerSec]);

  // reduced motion
  if (reduced) {
    return (
      <div
        className="w-full border-b bg-gradient-to-r from-blue-600 to-teal-500"
        style={{ borderColor: "rgb(var(--border))" }}
      >
        <div className="mx-auto max-w-7xl px-4 h-8 flex items-center overflow-hidden">
          <div className="text-xs text-white truncate">
            {items.map((x, i) => (
              <span key={i}>
                {i > 0 ? ` ${separator} ` : ""}
                {x.to ? (
                  <Link to={x.to} className="hover:underline text-white">
                    {x.text}
                  </Link>
                ) : (
                  <span className="text-[rgb(var(--text))]">{x.text}</span>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full border-b bg-[rgb(var(--bg))]"
      style={{ borderColor: "rgb(var(--border))" }}
    >
      <div
        ref={wrapRef}
        className="mx-auto max-w-7xl px-4 h-8 flex items-center overflow-hidden relative"
      >
        {/* edge fade */}
        <div className="absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-[rgb(var(--bg))] to-transparent pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-[rgb(var(--bg))] to-transparent pointer-events-none" />

        <motion.div
          className="flex items-center whitespace-nowrap"
          style={{ willChange: "transform" }}
          // ✅ pause
          animate={paused ? { x: 0 } : { x: [0, -trackW] }}
          transition={
            paused
              ? { duration: 0 }
              : { duration, repeat: Infinity, ease: "linear" }
          }
          onMouseEnter={() => pauseOnHover && setPaused(true)}
          onMouseLeave={() => pauseOnHover && setPaused(false)}
        >
          {/* ölçüm yapılan ana blok */}
          <div ref={trackRef} className="flex items-center gap-8 pr-8">
            {items.map((x, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-2 text-xs text-[rgb(var(--text-muted))]"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-teal-500 opacity-80" />
                {x.to ? (
                  <Link to={x.to} className="hover:underline text-[rgb(var(--text))]">
                    {x.text}
                  </Link>
                ) : (
                  <span className="text-[rgb(var(--text))]">{x.text}</span>
                )}
              </span>
            ))}
          </div>

          {/* kopyalar */}
          {Array.from({ length: repeatCount - 1 }).map((_, k) => (
            <div key={k} className="flex items-center gap-8 pr-8">
              {items.map((x, i) => (
                <span
                  key={`${k}-${i}`}
                  className="inline-flex items-center gap-2 text-xs text-[rgb(var(--text-muted))]"
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-teal-500 opacity-80" />
                  {x.to ? (
                    <Link to={x.to} className="hover:underline text-[rgb(var(--text))]">
                      {x.text}
                    </Link>
                  ) : (
                    <span className="text-[rgb(var(--text))]">{x.text}</span>
                  )}
                </span>
              ))}
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};
/* ─────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────── */
const HeaderPublic: React.FC = () => {
  const navigate = useNavigate();
  const { token, customer, logout } = useCustomerAuth();
  const { count, open } = useCart(); // open: cart sheet
  const { theme } = useTheme() as any; // var ama artık tek yerden CSS vars basacağız

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  const accountRef = useRef<HTMLDivElement>(null);

  const [cats, setCats] = useState<Category[]>([]);
  const [catsLoading, setCatsLoading] = useState(false);
  const [announcementItems, setAnnouncementItems] = useState<AnnouncementItem[]>(defaultAnnouncementItems);
  const [configLogoUrl, setConfigLogoUrl] = useState<string | null>(null);
  const [configSiteName, setConfigSiteName] = useState<string | null>(null);
  const [configSiteTagline, setConfigSiteTagline] = useState<string | null>(null);

  const catTree = useMemo(() => buildCategoryTree(cats), [cats]);

  const isBottomBarHidden = drawerOpen || mobileSearchOpen;

  const displayName =
    customer?.name || (customer?.id ? `Müşteri #${customer.id}` : "Hesabım");

  /* ─────────────────────────────────────────────────────────
     Tek yerden renk: ThemeContext => CSS Variables
     (Theme yoksa default mavi palet kalır)
  ────────────────────────────────────────────────────────── */
  useEffect(() => {
    const root = document.documentElement;

    // Default mavi palet (Theme gelmezse bile premium görünür)
    const defaults = {
      bg: "248 250 252",
      card: "255 255 255",
      border: "226 232 240",
      text: "15 23 42",
      textMuted: "100 116 139",
      primary: "37 99 235",
      primaryHover: "29 78 216",
      accent: "99 102 241",
      shadow: "0 10px 30px rgba(2,6,23,0.08)",
    };

    // ThemeContext varsa tek yer orası: buradan CSS vars bas
    // (Sende theme renkleri hex olabilir; basit map yaptım)
    const t = theme ?? {};

    // küçük helper: hex -> rgb
    const hexToRgb = (hex?: string) => {
      if (!hex || typeof hex !== "string") return null;
      const h = hex.replace("#", "").trim();
      if (h.length !== 6) return null;
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
      return `${r} ${g} ${b}`;
    };

    const bg = hexToRgb(t?.backgroundColor) ?? defaults.bg;
    const text = hexToRgb(t?.textPrimary) ?? defaults.text;
    const textMuted = hexToRgb(t?.textSecondary) ?? defaults.textMuted;

    // accent olarak mavimsi kullan (theme accent koyu yeşil ise bile header mavimsi kalmalı dedin)
    // istersen theme.accent’i primary’e bağlarız; şimdilik “mavi palet” sabit:
    const primary = defaults.primary;
    const primaryHover = defaults.primaryHover;
    const accent = defaults.accent;

    root.style.setProperty("--bg", bg);
    root.style.setProperty("--card", defaults.card);
    root.style.setProperty("--border", defaults.border);
    root.style.setProperty("--text", text);
    root.style.setProperty("--text-muted", textMuted);
    root.style.setProperty("--primary", primary);
    root.style.setProperty("--primary-hover", primaryHover);
    root.style.setProperty("--accent", accent);
    root.style.setProperty("--shadow", defaults.shadow);
  }, [theme]);

  /* ─────────────────────────────────────────────────────────
     ESC kapat + body scroll lock
  ────────────────────────────────────────────────────────── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setDrawerOpen(false);
      setMobileSearchOpen(false);
      setAccountOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const lock = drawerOpen || mobileSearchOpen;
    document.body.style.overflow = lock ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen, mobileSearchOpen]);

  /* ─────────────────────────────────────────────────────────
     Account dropdown outside click
  ────────────────────────────────────────────────────────── */
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!accountRef.current) return;
      if (!accountRef.current.contains(e.target as Node)) setAccountOpen(false);
    };
    if (accountOpen) document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [accountOpen]);

  /* ─────────────────────────────────────────────────────────
     Kategorileri getir
  ────────────────────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        setCatsLoading(true);
        const data = await fetchCategories();
        const filtered = (data ?? []).filter((d) => d.aktifMi !== false);
        filtered.sort((a, b) => (a.siraNo ?? 0) - (b.siraNo ?? 0));
        setCats(filtered);
      } catch (err) {
        console.error("Kategori listesi işlenirken hata oluştu:", err);
      } finally {
        setCatsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await fetchHeaderConfig();
        setAnnouncementItems(cfg.announcements);
        setConfigLogoUrl(cfg.logoUrl);
        setConfigSiteName(cfg.siteName);
        setConfigSiteTagline(cfg.siteTagline);
      } catch (err) {
        console.error("Header config state'i güncellenemedi:", err);
        setAnnouncementItems(defaultAnnouncementItems);
      }
    })();
  }, []);

  const brandLogoSrc = configLogoUrl || logo;
  const brandSiteName = configSiteName || "PORTAKAL3D";
  const brandSiteTagline = configSiteTagline || "3D Yazıcı & Filament";

  const goPanel = () => {
    setAccountOpen(false);
    navigate("/musteri/panel");
  };

  const doLogout = () => {
    setAccountOpen(false);
    logout();
    navigate("/");
  };

  const goCategory = useCallback(
    (c: Category) => {
      navigate(`/products?cat=${c.id}`);
      setDrawerOpen(false);
    },
    [navigate]
  );

  const navItem = (to: string, label: string) => (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        cn(
          "px-3 py-2 rounded-xl text-sm font-medium transition",
          "hover:bg-black/5",
          isActive &&
            "bg-gradient-to-r from-blue-600 to-teal-500 text-white hover:bg-[rgb(var(--primary-hover))]"
        )
      }
      onClick={() => setDrawerOpen(false)}
    >
      {label}
    </NavLink>
  );

  /* ─────────────────────────────────────────────────────────
     Mobile Bottom Bar (safe-area + compact)
  ────────────────────────────────────────────────────────── */
  const BottomBar = () => (
    <AnimatePresence>
      {!isBottomBarHidden && (
        <motion.nav
          key="mobile-bottom-bar"
          initial={{ y: 64, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 64, opacity: 0 }}
          transition={{ type: "tween", duration: 0.18 }}
          className="md:hidden fixed bottom-0 left-0 right-0 z-40"
        >
          <div className="mx-auto max-w-7xl px-3 pb-[calc(env(safe-area-inset-bottom,0px)+10px)]">
            <div
              className="rounded-2xl border bg-white/80 backdrop-blur-md shadow-xl"
              style={{ borderColor: "rgb(var(--border))" }}
            >
              <ul className="grid grid-cols-4">
                <li>
                  <NavLink
                    to="/"
                    end
                    className={({ isActive }) =>
                      cn(
                        "flex flex-col items-center justify-center py-3 gap-1 text-[11px]",
                        isActive ? "text-[rgb(var(--primary))]" : "text-[rgb(var(--text))]"
                      )
                    }
                    aria-label="Anasayfa"
                  >
                    <Home size={18} />
                    <span>Home</span>
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/products"
                    className={({ isActive }) =>
                      cn(
                        "flex flex-col items-center justify-center py-3 gap-1 text-[11px]",
                        isActive ? "text-[rgb(var(--primary))]" : "text-[rgb(var(--text))]"
                      )
                    }
                    aria-label="Ürünler"
                  >
                    <Boxes size={18} />
                    <span>Ürünler</span>
                  </NavLink>
                </li>

                <li>
                  <button
                    className="w-full flex flex-col items-center justify-center py-3 gap-1 text-[11px] text-[rgb(var(--text))]"
                    onClick={() => setMobileSearchOpen(true)}
                    aria-label="Ara"
                  >
                    <Search size={18} />
                    <span>Ara</span>
                  </button>
                </li>

                <li>
                  {token ? (
                    <button
                      className="w-full flex flex-col items-center justify-center py-3 gap-1 text-[11px] text-[rgb(var(--text))]"
                      onClick={() => navigate("/musteri/panel")}
                      aria-label="Hesabım"
                    >
                      <User2 size={18} />
                      <span>Hesap</span>
                    </button>
                  ) : (
                    <NavLink
                      to="/musteri/giris"
                      className={({ isActive }) =>
                        cn(
                          "flex flex-col items-center justify-center py-3 gap-1 text-[11px]",
                          isActive ? "text-[rgb(var(--primary))]" : "text-[rgb(var(--text))]"
                        )
                      }
                      aria-label="Giriş"
                    >
                      <User2 size={18} />
                      <span>Giriş</span>
                    </NavLink>
                  )}
                </li>
              </ul>
            </div>
          </div>
        </motion.nav>
      )}
    </AnimatePresence>
  );

  /* ─────────────────────────────────────────────────────────
     Mobile Search Full Overlay (kırılma yok)
  ────────────────────────────────────────────────────────── */
  const MobileSearchOverlay = () => (
    <AnimatePresence>
      {mobileSearchOpen && (
        <>
          <motion.div
            key="ms-overlay"
            className="fixed inset-0 z-[95] bg-black/35"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileSearchOpen(false)}
          />
          <motion.div
            key="ms-panel"
            className="fixed left-0 right-0 top-0 z-[100] bg-[rgb(var(--bg))] border-b"
            style={{ borderColor: "rgb(var(--border))" }}
            initial={{ y: -16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -16, opacity: 0 }}
            transition={{ type: "tween", duration: 0.16 }}
          >
            <div className="mx-auto max-w-7xl px-4 pt-3 pb-4">
              <div className="flex items-center gap-2">
                <button
                  className="h-11 w-11 rounded-xl border grid place-items-center bg-white"
                  style={{ borderColor: "rgb(var(--border))" }}
                  aria-label="Geri"
                  onClick={() => setMobileSearchOpen(false)}
                >
                  <ArrowLeft size={18} />
                </button>

                <div className="flex-1">
                  <SearchBox
                    autoFocus
                    className="w-full"
                    onResultSelect={() => setMobileSearchOpen(false)}
                  />
                </div>

                <button
                  className="h-11 w-11 rounded-xl border grid place-items-center bg-white"
                  style={{ borderColor: "rgb(var(--border))" }}
                  aria-label="Kapat"
                  onClick={() => setMobileSearchOpen(false)}
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {/* ✅ En üst açıklama / kayan yazı */}
      <AnnouncementBar
        items={announcementItems}
        speedPxPerSec={90}

      />

      <header
        className="sticky top-0 z-50 border-b bg-[rgb(var(--bg))]"
        style={{ borderColor: "rgb(var(--border))" }}
      >
        {/* MAIN BAR */}
        <div className="mx-auto max-w-7xl px-4">
          <div className="h-14 md:h-16 flex items-center gap-3">
            {/* Mobile menu */}
            <button
              className="md:hidden h-11 w-11 rounded-xl border grid place-items-center bg-white"
              style={{ borderColor: "rgb(var(--border))" }}
              aria-label="Menü"
              onClick={() => setDrawerOpen(true)}
            >
              <Menu size={18} />
            </button>

            {/* Logo (responsive) */}
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <span
                className="h-10 w-10 md:h-11 md:w-11 rounded-2xl border bg-white grid place-items-center overflow-hidden"
                style={{ borderColor: "rgb(var(--border))", boxShadow: "var(--shadow)" as any }}
              >
                <img src={brandLogoSrc} alt="Logo" className="w-full h-full object-contain" />
              </span>

              <span className=" flex flex-col leading-tight">
                <span className="font-bold  tracking-tight bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
                  {brandSiteName}
                </span>
                <span className="text-xs text-[rgb(var(--text-muted))] -mt-0.5">
                  {brandSiteTagline}
                </span>
              </span>
            </Link>

            {/* Desktop search */}
            <div className="hidden md:block flex-1 mx-3">

                <SearchBox />
            </div>

            {/* Desktop actions */}
            <div className="hidden md:flex items-center gap-2 ml-auto relative" ref={accountRef}>
              {/* Sepet butonu (istersen aç) */}
              <button
                onClick={open}
                className="relative h-11 px-3 rounded-xl border bg-white inline-flex items-center gap-2"
                style={{ borderColor: "rgb(var(--border))" }}
              >
                <span className="relative">
                  <ShoppingCart size={18} />
                  {count > 0 && (
                    <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] text-white grid place-items-center bg-[rgb(var(--primary))]">
                      {Math.min(count, 99)}
                    </span>
                  )}
                </span>
                <span className="text-sm font-semibold">Sepet</span>
              </button>

              {!token ? (
                <>
                  <Link
                    to="/musteri/giris"
                    className="h-11 px-4 rounded-xl border bg-white inline-flex items-center justify-center text-sm font-semibold"
                    style={{ borderColor: "rgb(var(--border))" }}
                  >
                    Giriş
                  </Link>
                  <Link
                    to="/musteri/kayit"
                    className="h-11 px-4 rounded-xl inline-flex items-center justify-center text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-teal-500 transition"
                  >
                    Kayıt Ol
                  </Link>
                </>
              ) : (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setAccountOpen((s) => !s);
                    }}
                    className="h-11 px-3 rounded-xl border bg-white inline-flex items-center gap-2"
                    style={{ borderColor: "rgb(var(--border))" }}
                    aria-haspopup="menu"
                    aria-expanded={accountOpen}
                  >
                    <User2 size={18} />
                    <span className="text-sm font-semibold truncate max-w-[12rem]">
                      {customer?.name || displayName}
                    </span>
                    <ChevronDown size={16} className="opacity-70" />
                  </button>

                  <AnimatePresence>
                    {accountOpen && (
                      <motion.div
                        key="account-menu"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ type: "tween", duration: 0.15 }}
                        className="absolute right-0 top-[calc(100%+10px)] w-60 rounded-2xl border bg-white shadow-xl overflow-hidden"
                        style={{ borderColor: "rgb(var(--border))",zIndex:999999 }}
                        role="menu"
                      >
                        <div className="px-4 py-3 border-b" style={{ borderColor: "rgb(var(--border))" }}>
                          <div className="text-xs text-[rgb(var(--text-muted))]">Hesap</div>
                          <div className="text-sm font-normal  text-[rgb(var(--text))] truncate">
                            {customer?.name ? customer.name : `Müşteri #${customer?.id}`}
                          </div>
                        </div>

                        <button
                          onClick={goPanel}
                          className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-black/5"
                          role="menuitem"
                        >
                          <User2 size={16} />
                          <span className="text-sm font-semibold">Panele Git</span>
                        </button>

                        <button
                          onClick={doLogout}
                          className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-black/5"
                          role="menuitem"
                        >
                          <LogOut size={16} />
                          <span className="text-sm font-semibold">Çıkış Yap</span>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}
            </div>

            {/* Mobile: search icon */}
            <button
              className="md:hidden ml-auto h-11 w-11 rounded-xl border grid place-items-center bg-white"
              style={{ borderColor: "rgb(var(--border))" }}
              aria-label="Ara"
              onClick={() => setMobileSearchOpen(true)}
            >
              <Search size={18} />
            </button>
          </div>
        </div>

        {/* Desktop NAV BAR */}
        <div className="hidden md:block border-t bg-white/60 backdrop-blur"
             style={{ borderColor: "rgb(var(--border))" }}>
          <div className="mx-auto max-w-7xl px-4">
            <div className="h-12 flex items-center gap-2">
              <nav className="flex items-center gap-1">
                {navItem("/", "Anasayfa")}

                {/* Root kategoriler - max MAX_NAV_CATS adet */}
                {catTree.slice(0, MAX_NAV_CATS).map((root) => (
                  <DesktopMegaMenuItem key={root.id} node={root} onGo={goCategory} />
                ))}

                {/* Tüm Kategoriler butonu */}
                {catTree.length > MAX_NAV_CATS && (
                  <AllCategoriesMenu cats={catTree} onGo={goCategory} />
                )}

                {navItem("/projeler", "Projelerimiz")}
                {navItem("/iletisim", "İletişim")}
              </nav>

              <div className="ml-auto text-xs text-[rgb(var(--text-muted))]">
                {catsLoading ? "Kategoriler yükleniyor..." : ""}
              </div>
            </div>
          </div>
        </div>

        {/* İnce accent çizgi */}
        <div className="h-[3px] w-full bg-gradient-to-r from-blue-600 to-teal-500" />

        {/* Mobile Search overlay */}
        <MobileSearchOverlay />

        {/* Mobile Drawer */}
        <AnimatePresence>
          {drawerOpen && (
            <>
              <motion.div
                key="overlay"
                className="fixed inset-0 z-[90] bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setDrawerOpen(false)}
              />
              <motion.aside
                key="drawer"
                className="fixed top-0 left-0 h-full w-[86%] max-w-sm z-[100] shadow-2xl flex flex-col bg-[rgb(var(--bg))] border-r"
                style={{ borderColor: "rgb(var(--border))" }}
                initial={{ x: -28, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -28, opacity: 0 }}
                transition={{ type: "spring", stiffness: 380, damping: 32 }}
              >
                {/* Drawer header */}
                <div className="px-4 py-4 flex items-center justify-between border-b bg-white/70 backdrop-blur"
                     style={{ borderColor: "rgb(var(--border))" }}>
                  <Link
                    to="/"
                    onClick={() => setDrawerOpen(false)}
                    className="flex items-center gap-3"
                  >
                    <span
                      className="h-10 w-10 rounded-2xl border bg-white grid place-items-center overflow-hidden"
                      style={{ borderColor: "rgb(var(--border))" }}
                    >
                      <img src={brandLogoSrc} alt={brandSiteName} className="w-full h-full object-contain" />
                    </span>
                    <div className="leading-tight">
                      <div className="font-normal  text-[rgb(var(--text))]">{brandSiteName}</div>
                      <div className="text-xs text-[rgb(var(--text-muted))]">Menü</div>
                    </div>
                  </Link>

                  <button
                    className="h-10 w-10 rounded-xl border bg-white grid place-items-center"
                    style={{ borderColor: "rgb(var(--border))" }}
                    onClick={() => setDrawerOpen(false)}
                    aria-label="Kapat"
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Drawer search (autofocus YOK: mobil klavye patlatmasın) */}
                <div className="p-4 border-b" style={{ borderColor: "rgb(var(--border))" }}>
                  <div
                    className="rounded-2xl border bg-white px-3 py-2"
                    style={{ borderColor: "rgb(var(--border))" }}
                  >
                    <SearchBox onResultSelect={() => setDrawerOpen(false)} />
                  </div>
                </div>

                {/* Drawer content */}
                <div className="flex-1 overflow-auto px-4 py-4">
                  <div className="flex flex-col gap-1">
                    <MobileSimpleLink
                      to="/"
                      label="Anasayfa"
                      onClick={() => setDrawerOpen(false)}
                    />
                    <MobileSimpleLink
                      to="/products"
                      label="Ürünlerimiz"
                      onClick={() => setDrawerOpen(false)}
                    />

                    {/* Kategoriler */}
                    <div className="mt-2">
                      <div className="px-2 py-2 text-xs font-semibold text-[rgb(var(--text-muted))]">
                        Kategoriler
                      </div>
                      <div className="flex flex-col gap-1">
                        {catTree.map((root) => (
                          <MobileRootItem
                            key={root.id}
                            node={root}
                            go={(c) => goCategory(c)}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="mt-2">
                      <MobileSimpleLink
                        to="/projeler"
                        label="Projelerimiz"
                        onClick={() => setDrawerOpen(false)}
                      />
                      <MobileSimpleLink
                        to="/iletisim"
                        label="İletişim"
                        onClick={() => setDrawerOpen(false)}
                      />
                    </div>
                  </div>
                </div>

                {/* Drawer footer actions */}
                <div className="p-4 border-t bg-white/70 backdrop-blur"
                     style={{ borderColor: "rgb(var(--border))" }}>
                  {!token ? (
                    <div className="grid grid-cols-2 gap-2">
                      <Link
                        to="/musteri/giris"
                        onClick={() => setDrawerOpen(false)}
                        className="h-11 rounded-xl border bg-white grid place-items-center font-semibold"
                        style={{ borderColor: "rgb(var(--border))" }}
                      >
                        Giriş
                      </Link>
                      <Link
                        to="/musteri/kayit"
                        onClick={() => setDrawerOpen(false)}
                        className="h-11 rounded-xl grid place-items-center font-semibold text-white bg-gradient-to-r from-blue-600 to-teal-500 transition"
                      >
                        Kayıt Ol
                      </Link>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => {
                          setDrawerOpen(false);
                          navigate("/musteri/panel");
                        }}
                        className="h-11 rounded-xl border bg-white grid place-items-center font-semibold"
                        style={{ borderColor: "rgb(var(--border))" }}
                      >
                        Panel
                      </button>
                      <button
                        onClick={() => {
                          setDrawerOpen(false);
                          doLogout();
                        }}
                        className="h-11 rounded-xl grid place-items-center font-semibold text-white bg-gradient-to-r from-blue-600 to-teal-500 transition"
                      >
                        Çıkış
                      </button>
                    </div>
                  )}
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        <BottomBar />
      </header>

      {/* Sepet sheet (sende zaten var) */}
      <CartSheet />
    </>
  );
};

export default HeaderPublic;

/* ─────────────────────────────────────────────────────────
   Desktop Category Components (hover tree)
───────────────────────────────────────────────────────── */
const DesktopCategoryItem: React.FC<{
  node: CategoryNode;
  onGo: (c: Category) => void;
}> = ({ node, onGo }) => {
  const hasChildren = node.children.length > 0;

  return (
    <div className="relative group">
      <button
        onClick={() => onGo(node)}
        className="px-3 py-2 rounded-xl text-sm  text-[rgb(var(--text))] hover:bg-black/5 transition inline-flex items-center gap-1"
      >
        {node.ad}
        {hasChildren && <ChevronDown size={14} className="opacity-60" />}
      </button>

      {hasChildren && (
        <div className="absolute left-0 top-full pt-2 hidden group-hover:block">
          <DesktopSubTree node={node} onGo={onGo} />
        </div>
      )}
    </div>
  );
};

const DesktopSubTree: React.FC<{
  node: CategoryNode;
  onGo: (c: Category) => void;
}> = ({ node, onGo }) => (
  <ul
    className="w-[300px] max-h-[420px] overflow-auto rounded-2xl border bg-white shadow-2xl"
    style={{ borderColor: "rgb(var(--border))" }}
  >
    {node.children.map((ch) => (
      <li key={ch.id} className="relative group">
        <button
          onClick={() => onGo(ch)}
          className="w-full text-left px-4 py-3 text-sm hover:bg-black/5 transition flex items-center justify-between"
        >
          <span className="truncate text-[rgb(var(--text))]">{ch.ad}</span>
          {ch.children.length > 0 && (
            <ChevronRight size={14} className="opacity-60" />
          )}
        </button>

        {ch.children.length > 0 && (
          <div className="absolute left-full top-0 ml-2 hidden group-hover:block">
            <DesktopSubTree node={ch} onGo={onGo} />
          </div>
        )}
      </li>
    ))}
  </ul>
);

/* ─────────────────────────────────────────────────────────
   Mobile Drawer Menu Components
───────────────────────────────────────────────────────── */
const MobileSimpleLink: React.FC<{
  to: string;
  label: string;
  onClick?: () => void;
}> = ({ to, label, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    className={({ isActive }) =>
      cn(
        "px-3 py-2.5 rounded-xl text-sm font-semibold transition",
        isActive
          ? "bg-gradient-to-r from-blue-600 to-teal-500 text-white"
          : "text-[rgb(var(--text))] hover:bg-black/5"
      )
    }
  >
    {label}
  </NavLink>
);

const MobileRootItem: React.FC<{
  node: CategoryNode;
  go: (c: Category) => void;
}> = ({ node, go }) => {
  const [open, setOpen] = useState(false);
  const has = node.children.length > 0;

  return (
    <div className="rounded-xl border bg-white/70"
         style={{ borderColor: "rgb(var(--border))" }}>
      <div className="flex items-center justify-between px-3 py-2.5">
        <button
          onClick={() => go(node)}
          className="text-left font-semibold text-[rgb(var(--text))] truncate"
        >
          {node.ad}
        </button>

        {has && (
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label="Alt menü"
            className="h-9 w-9 rounded-xl border bg-white grid place-items-center"
            style={{ borderColor: "rgb(var(--border))" }}
          >
            <ChevronDown
              size={16}
              className={cn("transition-transform", open && "rotate-180")}
            />
          </button>
        )}
      </div>

      {has && open && (
        <div className="pb-2">
          {node.children.map((ch) => (
            <MobileTree key={ch.id} node={ch} go={go} depth={1} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   Desktop — "Tüm Kategoriler" Full-Width Mega Menu
   Hover ile açılır, sol liste + sağ grid yapısı
───────────────────────────────────────────────────────── */
const AllCategoriesMenu: React.FC<{
  cats: CategoryNode[];
  onGo: (c: Category) => void;
}> = ({ cats, onGo }) => {
  const [open, setOpen] = useState(false);
  const [activeRoot, setActiveRoot] = useState<CategoryNode | null>(cats[0] ?? null);
  const [panelTop, setPanelTop] = useState(0);

  const btnRef = useRef<HTMLButtonElement>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /* ── Hover yönetimi: button + panel arasında geçişte kapanmasın ── */
  const openMenu = useCallback(() => {
    clearTimeout(hoverTimer.current);
    const btn = btnRef.current;
    if (btn) {
      const r = btn.getBoundingClientRect();
      // Butonun hemen altından başlasın (3px accent çizgisi dahil)
      setPanelTop(Math.round(r.bottom));
    }
    setOpen(true);
  }, []);

  const scheduleClose = useCallback(() => {
    hoverTimer.current = setTimeout(() => setOpen(false), 120);
  }, []);

  const cancelClose = useCallback(() => {
    clearTimeout(hoverTimer.current);
  }, []);

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  /* cats değişirse ilk kategoriyi seç */
  useEffect(() => {
    if (cats.length > 0 && !activeRoot) setActiveRoot(cats[0]);
  }, [cats, activeRoot]);

  const go = useCallback((node: CategoryNode) => {
    onGo(node);
    setOpen(false);
  }, [onGo]);

  /* Aktif kök kategorinin alt kategorilerini sütunlara böl */
  const subcats = activeRoot?.children ?? [];
  const subCols = useMemo(() => {
    if (subcats.length === 0) return [];
    const numCols = Math.min(4, Math.max(1, Math.ceil(subcats.length / 5)));
    const perCol = Math.ceil(subcats.length / numCols);
    return chunk(subcats, Math.max(1, perCol));
  }, [subcats]);

  return (
    <>
      {/* ── Trigger Butonu ── */}
      <button
        ref={btnRef}
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
        className={cn(
          "px-3 py-2 rounded-xl text-sm font-medium transition-all duration-150 inline-flex items-center gap-1.5",
          open
            ? "bg-gradient-to-r from-blue-600 to-teal-500 text-white"
            : "text-[rgb(var(--text))] hover:bg-black/5"
        )}
      >
        <Boxes size={15} className={open ? "opacity-100" : "opacity-60"} />
        Tüm Kategoriler
        <ChevronDown
          size={14}
          className={cn(
            "transition-transform duration-200",
            open ? "rotate-180 opacity-100" : "opacity-50"
          )}
        />
      </button>

      {/* ── Mega Menu Panel (fixed, tam genişlik) ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mega-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ type: "tween", duration: 0.18 }}
            className="fixed left-0 right-0 z-[9999]"
            style={{ top: "50px" }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            {/* Üstte ince gradient çizgi */}
            <div className="h-[3px] bg-gradient-to-r from-blue-600 to-teal-500" />

            {/* Ana panel */}
            <div
              className="bg-white/95 backdrop-blur-md border-b"
              style={{
                borderColor: "rgb(var(--border))",
                boxShadow: "0 20px 60px rgba(2,6,23,0.12)",
              }}
            >
              <div className="mx-auto max-w-7xl px-4">
                <div className="flex" style={{ minHeight: 340 }}>

                  {/* ──────────────────────────────────────
                      SOL: Kök kategori listesi
                  ────────────────────────────────────── */}
                  <div
                    className="w-56 shrink-0 border-r py-4 overflow-y-auto"
                    style={{ borderColor: "rgb(var(--border))", maxHeight: 660 }}
                  >
                    <div className="px-4 pb-3 text-[10px] font-bold uppercase tracking-[0.15em] text-[rgb(var(--text-muted))]">
                      Kategoriler
                    </div>

                    {cats.map((node) => {
                      const isActive = activeRoot?.id === node.id;
                      return (
                        <button
                          key={node.id}
                          onMouseEnter={() => setActiveRoot(node)}
                          onClick={() => go(node)}
                          className={cn(
                            "w-full text-left flex items-center justify-between gap-2 transition-all duration-100",
                            "mx-2 rounded-xl px-3 py-2.5 text-sm",
                            isActive
                              ? "bg-gradient-to-r from-blue-600 to-teal-500 text-white font-semibold shadow-sm"
                              : "text-[rgb(var(--text))] hover:bg-slate-50"
                          )}
                          style={{ width: "calc(100% - 16px)" }}
                        >
                          <span className="truncate">{node.ad}</span>
                          {node.children.length > 0 && (
                            <ChevronRight
                              size={13}
                              className={cn("shrink-0 transition-opacity", isActive ? "opacity-90" : "opacity-30")}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* ──────────────────────────────────────
                      SAĞ: Alt kategori grid alanı
                  ────────────────────────────────────── */}
                  <div className="flex-1 py-5 px-6 overflow-y-auto" style={{ maxHeight: 460 }}>
                    {activeRoot && (
                      <>
                        {/* Başlık satırı */}
                        <div className="flex items-center justify-between mb-5">
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-[rgb(var(--text-muted))] mb-0.5">
                              Kategori
                            </div>
                            <div className="text-lg font-bold text-[rgb(var(--text))] leading-tight">
                              {activeRoot.ad}
                            </div>
                          </div>

                          <button
                            onClick={() => go(activeRoot)}
                            className="h-9 px-5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-teal-500 hover:opacity-90 transition-opacity inline-flex items-center gap-1.5 shadow-sm"
                          >
                            Tümünü Gör
                            <ChevronRight size={13} />
                          </button>
                        </div>

                        {/* Ayraç */}
                        <div className="h-px bg-gradient-to-r from-blue-100 via-teal-100 to-transparent mb-5" />

                        {/* Alt kategoriler grid */}
                        {subCols.length > 0 ? (
                          <div
                            className="grid gap-x-8"
                            style={{ gridTemplateColumns: `repeat(${subCols.length}, minmax(0, 1fr))` }}
                          >
                            {subCols.map((col, ci) => (
                              <div key={ci} className="space-y-5">
                                {col.map((sub) => (
                                  <div key={sub.id}>
                                    {/* Alt kategori başlığı */}
                                    <button
                                      onClick={() => go(sub)}
                                      className="text-sm font-bold text-[rgb(var(--text))] hover:text-blue-600 transition-colors mb-2 text-left w-full truncate group flex items-center gap-1"
                                    >
                                      <span className="truncate">{sub.ad}</span>
                                      {sub.children.length > 0 && (
                                        <ChevronRight size={12} className="opacity-0 group-hover:opacity-60 transition-opacity shrink-0" />
                                      )}
                                    </button>

                                    {/* Yaprak kategoriler */}
                                    {sub.children.length > 0 && (
                                      <ul className="space-y-1.5">
                                        {sub.children.slice(0, 5).map((leaf) => (
                                          <li key={leaf.id}>
                                            <button
                                              onClick={() => go(leaf)}
                                              className="text-xs text-[rgb(var(--text-muted))] hover:text-blue-600 transition-colors text-left w-full truncate block"
                                            >
                                              {leaf.ad}
                                            </button>
                                          </li>
                                        ))}
                                        {sub.children.length > 5 && (
                                          <li>
                                            <button
                                              onClick={() => go(sub)}
                                              className="text-xs font-semibold text-blue-500 hover:text-blue-700 hover:underline transition-colors"
                                            >
                                              +{sub.children.length - 5} daha
                                            </button>
                                          </li>
                                        )}
                                      </ul>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        ) : (
                          /* Alt kategorisi olmayan kök kategori */
                          <div className="flex flex-col items-center justify-center h-40 gap-3">
                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-50 to-teal-50 grid place-items-center">
                              <Boxes size={22} className="text-blue-400" />
                            </div>
                            <div className="text-sm text-[rgb(var(--text-muted))] text-center">
                              <div className="font-semibold text-[rgb(var(--text))] mb-1">{activeRoot.ad}</div>
                              <div>Bu kategorideki tüm ürünleri görmek için tıklayın</div>
                            </div>
                            <button
                              onClick={() => go(activeRoot)}
                              className="h-9 px-5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-teal-500 hover:opacity-90 transition-opacity"
                            >
                              Ürünleri Gör
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Alt footer çizgisi */}
              <div
                className="mx-auto max-w-7xl px-4 py-2.5 border-t flex items-center justify-between"
                style={{ borderColor: "rgb(var(--border))" }}
              >
                <span className="text-xs text-[rgb(var(--text-muted))]">
                  Kategoriye tıklayarak ürünleri filtrele
                </span>
                <button
                  onClick={() => { setOpen(false); window.location.href = "/products"; }}
                  className="text-xs font-semibold text-blue-600 hover:underline"
                >
                  Tüm Ürünlere Git →
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const MobileTree: React.FC<{
  node: CategoryNode;
  go: (c: Category) => void;
  depth: number;
}> = ({ node, go, depth }) => {
  const [open, setOpen] = useState(depth < 2);
  const has = node.children.length > 0;

  return (
    <div className="pl-2">
      <div className="flex items-center justify-between px-3 py-2"
           style={{ marginLeft: depth * 8 }}>
        <button
          onClick={() => go(node)}
          className="text-left text-sm font-medium text-[rgb(var(--text))] truncate"
        >
          {node.ad}
        </button>

        {has && (
          <button
            onClick={() => setOpen((v) => !v)}
            className="h-8 w-8 rounded-xl border bg-white grid place-items-center"
            style={{ borderColor: "rgb(var(--border))" }}
            aria-label="Alt kategori"
          >
            <ChevronDown
              size={14}
              className={cn("transition-transform", open && "rotate-180")}
            />
          </button>
        )}
      </div>

      {has && open && (
        <div className="pb-1">
          {node.children.map((ch) => (
            <MobileTree key={ch.id} node={ch} go={go} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
};


/* ─────────────────────────────────────────────────────────
   Desktop Category Components (Mega Menu - kurumsal)
───────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────
   Desktop Category Components (Mega Menu - kurumsal)
───────────────────────────────────────────────────────── */

const MAX_NAV_CATS = 4; // nav bar'da gösterilecek max kategori sayısı
const MAX_COLS = 3;

function chunk<T>(arr: T[], size: number) {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

const VIEWPORT_PAD = 20;

const PANEL_W = 820;
const PAD = 16;



type AlignMode = "left" | "right" | "center";

const DesktopMegaMenuItem: React.FC<{
  node: CategoryNode;
  onGo: (c: Category) => void;
}> = ({ node, onGo }) => {
  const hasChildren = node.children.length > 0;
  const sections = node.children;

  const cols = useMemo(() => {
    if (!hasChildren) return [];
    const perCol = Math.ceil(sections.length / MAX_COLS);
    return chunk(sections, Math.max(1, perCol));
  }, [hasChildren, sections]);

  const btnRef = React.useRef<HTMLButtonElement | null>(null);
  const [align, setAlign] = React.useState<AlignMode>("left");

  const decideAlign = React.useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;

    const r = btn.getBoundingClientRect();
    const panelWidth = Math.min(PANEL_W, window.innerWidth - PAD * 2);

    // 1) left-0 ile açınca sağdan taşar mı?
    const fitsLeft = r.left + panelWidth <= window.innerWidth - PAD;

    // 2) right-0 ile açınca soldan taşar mı?
    // right-0 demek: panelin sağ ucu butonun sağında.
    // panelLeft = r.right - panelWidth; bu PAD'den büyük olmalı
    const fitsRight = r.right - panelWidth >= PAD;

    if (fitsLeft) setAlign("left");
    else if (fitsRight) setAlign("right");
    else setAlign("center"); // çok dar ekran: ortala
  }, []);

  return (
    <div className="relative group" onMouseEnter={decideAlign}>
      <button
        ref={btnRef}
        onClick={() => onGo(node)}
        className={cn(
          "px-3 py-2 rounded-xl text-sm font-medium transition inline-flex items-center gap-1",
          "text-[rgb(var(--text))] hover:bg-black/5 max-w-[140px]"
        )}
      >
        <span className="truncate">{node.ad}</span>
        {hasChildren && <ChevronDown size={14} className="opacity-60 shrink-0" />}
      </button>

      {hasChildren && (
        <>
          {/* hover bridge */}
          <div className="absolute left-0 right-0 top-full h-3 hidden group-hover:block" />

          {/* dropdown wrapper */}
          <div
            className={cn(
              "absolute top-full z-50 hidden group-hover:block",
              align === "left" && "left-0",
              align === "right" && "right-0",
              align === "center" && "left-1/2 -translate-x-1/2"
            )}
          >
            <div
              className={cn(
                "w-[min(820px,calc(100vw-32px))]",
                "rounded-2xl border bg-white shadow-2xl overflow-hidden"
              )}
              style={{ borderColor: "rgb(var(--border))" }}
            >
              {/* header */}
              <div
                className="px-5 py-4 border-b bg-white/80"
                style={{ borderColor: "rgb(var(--border))" }}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-xs text-[rgb(var(--text-muted))]">
                      Kategori
                    </div>
                    <div className="text-sm font-semibold text-[rgb(var(--text))] truncate">
                      {node.ad}
                    </div>
                  </div>

                  <button
                    onClick={() => onGo(node)}
                    className="h-9 px-3 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-teal-500"
                  >
                    Tümünü Gör
                  </button>
                </div>
              </div>

              {/* body */}
              <div className="max-h-[420px] overflow-auto">
                <div className="p-5">
                  <div
                    className="grid gap-6"
                    style={{
                      gridTemplateColumns: `repeat(${cols.length || 1}, minmax(0, 1fr))`,
                    }}
                  >
                    {cols.map((col, ci) => (
                      <div key={ci} className="space-y-6">
                        {col.map((sec) => (
                          <div key={sec.id} className="space-y-2">
                            <button
                              onClick={() => onGo(sec)}
                              className={cn(
                                "w-full text-left",
                                "text-sm font-semibold text-[rgb(var(--text))]",
                                "hover:underline underline-offset-4"
                              )}
                              title={sec.ad}
                            >
                              {sec.ad}
                            </button>

                            {sec.children?.length > 0 ? (
                              <ul className="space-y-1">
                                {sec.children.map((leaf) => (
                                  <li key={leaf.id}>
                                    <button
                                      onClick={() => onGo(leaf)}
                                      className={cn(
                                        "w-full text-left text-sm",
                                        "text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text))]",
                                        "px-2 py-1 rounded-lg hover:bg-black/5 transition",
                                        "truncate"
                                      )}
                                      title={leaf.ad}
                                    >
                                      {leaf.ad}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="text-xs text-[rgb(var(--text-muted))] opacity-80">
                                
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* footer */}
              <div
                className="px-5 py-3 border-t bg-white/70"
                style={{ borderColor: "rgb(var(--border))" }}
              >
                <div className="flex items-center justify-between">
                  <div className="text-xs text-[rgb(var(--text-muted))]">
                    Seçim yaparak ürünleri filtreleyebilirsin
                  </div>
                  <button
                    onClick={() => onGo(node)}
                    className="text-xs font-semibold text-[rgb(var(--primary))] hover:underline underline-offset-4"
                  >
                    {node.ad} ›
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
